<script>
import { format, parseISO } from 'date-fns';
import { mapActions } from 'vuex';
import { useAlert } from 'dashboard/composables';

export default {
  name: "GSheetCard",
  props: {
    knowledge: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isSyncing: false,
    };
  },
  methods: {
    ...mapActions('gsheet', ['syncGSheetKB']),
    async handleSync() {
      try {
        this.isSyncing = true;
        useAlert('Syncing...');
        await this.syncGSheetKB(this.knowledge.id);
        this.$emit('refresh');
      } catch (err) {
        if (Array.isArray(err)) {
          useAlert(err.join('\n'), { type: 'error' });
        } else {
          useAlert('Failed to sync', { type: 'error' });
          console.error(err);
        }
      } finally {
        this.isSyncing = false;
      }
    },
    handleRoute() {
      this.$router.push({
        name: 'google_sheet_details',
        params: {
          accountId: this.$route.params.accountId,
          GSheetKBId: this.knowledge.id,
        },
      });
    },
    formatDate(date){
      if (!date) return "N/A";
      const isoDate = parseISO(date);
      const formatted = format(isoDate, "dd/MM/yyyy, hh:mm a");
      return formatted
    }
  }
}
</script>

<template>
  <div 
    @click="handleRoute"
    class="cursor-pointer relative bg-white dark:bg-slate-800 hover:bg-woot-50 dark:hover:bg-slate-700 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 rounded-lg border border-slate-200 dark:border-slate-700 flex flex-col group"
  >
    <!-- Header -->
    <div class="p-4 border-b border-slate-100 dark:border-slate-700">
      <h4 
        class="text-base font-medium text-slate-900 dark:text-slate-100 truncate"
        :title="knowledge.name"
      >
        {{ knowledge.name }}
      </h4>
      <p class="text-sm text-slate-600 dark:text-slate-400 truncate">
        ID: {{ knowledge.id }}
      </p>
    </div>

    <!-- Content -->
    <div class="flex-1 p-4 flex flex-col">
      <!-- Status & Sync Info -->
      <p class="text-sm text-slate-600 dark:text-slate-300">
        Status: 
        <span 
          :class="{
            'text-green-600 dark:text-green-400': knowledge.sync_status === 'completed',
            'text-yellow-600 dark:text-yellow-400': knowledge.sync_status === 'idle' || knowledge.sync_status === 'syncing',
            'text-red-600 dark:text-red-400': knowledge.sync_status === 'failed'
          }"
        >
          {{ knowledge.sync_status.toUpperCase() }}
        </span>
      </p>
      <p 
        v-if="knowledge.sync_error_message" 
        class="text-xs text-red-500 dark:text-red-400 mt-1 line-clamp-2"
      >
        Error: {{ knowledge.sync_error_message }}
      </p>

      <!-- Description -->
      <p class="text-sm text-slate-600 dark:text-slate-300 mt-3 line-clamp-2">
        {{ knowledge.description || '-' }}
      </p>

      <!-- Created/Updated -->
      <p class="text-xs text-slate-500 dark:text-slate-400 mt-3">
        Created: {{ formatDate(knowledge.created_at) }}
      </p>
      <p class="text-xs text-slate-500 dark:text-slate-400">
        Updated: {{ formatDate(knowledge.updated_at) }}
      </p>
      <p v-if="knowledge.last_synced_at" class="text-xs text-slate-500 dark:text-slate-400 mt-1">
        Last synced: {{ formatDate(knowledge.last_synced_at) }}
      </p>

      <!-- Actions -->
      <div class="mt-3 pt-2 border-t border-slate-100 dark:border-slate-700 flex gap-4 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
        <woot-button
          color-scheme="secondary"
          size="small"
          icon="edit"
          @click.stop="$emit('edit', knowledge)"
          class="hover:bg-slate-700 dark:hover:bg-slate-500 w-full md:w-auto"
          v-tooltip.top="'Edit Description'"
        />
        <a :href="knowledge.gsheet_url" target="_blank">
          <woot-button
            icon="open"
            size="small"
            class="hover:bg-woot-700 dark:hover:bg-woot-500 w-full md:w-auto"
            v-tooltip.top="'Open Google Sheet'"
          />
        </a>
        <woot-button
          variant="primary"
          size="small"
          icon="arrow-rotate-clockwise"
          :is-loading="isSyncing"
          @click.stop="handleSync"
          class="hover:bg-woot-700 dark:hover:bg-woot-500 w-full md:w-auto"
          v-tooltip.top="'Sync'"
        />
        <woot-button 
          @click.stop="$emit('delete', knowledge)" 
          icon="delete" 
          size="small" 
          color-scheme="alert"
          class="hover:bg-red-600 dark:hover:bg-red-500"
          v-tooltip.top="'Delete'"
        />
      </div>
    </div>
  </div>
</template>


